import { Suspense, useEffect } from 'react';
import type { FC } from 'react';
import { useIntl } from 'react-intl';

import type { CompaniesIndustry } from '@xing-com/crate-common-graphql-types';

import { IndustryCard } from '../../components/industry-card/industry-card';
import { SubpageLayout } from '../../components/layouts/subpage-layout';
import { useAllIndustries } from '../../hooks/use-companies-industries';
import { useTrackPage } from '../../hooks/use-track-page';
import { createCompaniesIndustries } from '../../models/industry';
import {
  pagesTrackingName,
  useNwtTrackingCompanies,
} from '../../utils/tracking';

import * as Styled from './industries.styles';

const SKELETON_CARDS_NUMBER = 24;

const IndustriesInner: FC = () => {
  const { orderedIndustries } = useAllIndustries();

  const collection = (orderedIndustries ?? []) as CompaniesIndustry[];
  const industriesCollection = createCompaniesIndustries(collection);

  return industriesCollection.map((item) => (
    <IndustryCard
      key={item.id}
      item={item}
      trackingOptions={{
        eventSchema: 'basic',
        event: 'opened',
        page: 'companies/industries',
        itemUrn: `surn:x-xing:companies:industry:${item.seoName}`,
      }}
    />
  ));
};

export const Industries: FC = () => {
  const { formatMessage } = useIntl();
  const trackNwtEvent = useNwtTrackingCompanies();
  useTrackPage({ pageName: pagesTrackingName.industries });

  useEffect(() => {
    trackNwtEvent({
      eventSchema: 'basic',
      page: 'companies/industries',
      event: 'viewed_screen',
    });
  }, []);

  return (
    <SubpageLayout
      headerText={formatMessage({ id: 'CP_INDUSTRIES_SECTION_TITLE' })}
      description={formatMessage({ id: 'CP_INDUSTRIES_SECTION_SUBLINE' })}
    >
      <Styled.GridIndustries>
        <Suspense
          fallback={
            <>
              {Array.from({ length: SKELETON_CARDS_NUMBER }).map((_, index) => (
                <IndustryCard.Skeleton key={index} />
              ))}
            </>
          }
        >
          <IndustriesInner />
        </Suspense>
      </Styled.GridIndustries>
    </SubpageLayout>
  );
};
